import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import { canChangeWhatsAppConsent } from '@/lib/permissions'
import { formatDateTime } from '@/lib/utils'
import {
  useChangeWhatsAppConsentMutation,
  useWhatsAppConsentQuery,
  type WhatsAppConsent
} from '@/services/whatsapp-consent'
import { borderRadius, colors, sxPresets } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material'
import { useState } from 'react'

const SOURCE_LABELS: Record<WhatsAppConsent['source'], string> = {
  checkout: 'Checkout',
  'whatsapp-reply': 'WhatsApp reply (STOP)',
  support: 'Support'
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant='fieldLabel' sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant='fieldValue'>{value}</Typography>
    </Box>
  )
}

export default function UserWhatsAppConsent({ customerHash }: { customerHash: string }) {
  const { data: consent, isLoading, isError } = useWhatsAppConsentQuery(customerHash)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const renderContent = () => {
    if (isLoading) return <InlineLoadingSpinner text='Loading WhatsApp choice...' />
    if (isError) return <Typography variant='smallText'>Could not load the WhatsApp choice.</Typography>
    if (!consent) {
      return (
        <Typography variant='smallText'>
          This account has never made a WhatsApp choice, so no WhatsApp messages are sent.
        </Typography>
      )
    }

    return (
      <Box
        sx={{
          ...sxPresets().grid.fourColumn,
          '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
        }}
      >
        <Field label='WhatsApp' value={consent.optedIn ? 'On' : 'Off'} />
        <Field label='Number' value={consent.phone ?? 'No UK mobile'} />
        <Field label='Chosen on' value={formatDateTime(consent.decidedAt)} />
        <Field
          label='Chosen via'
          value={
            consent.agent ? `${SOURCE_LABELS[consent.source]} (${consent.agent.name})` : SOURCE_LABELS[consent.source]
          }
        />
      </Box>
    )
  }

  return (
    <Paper
      id='whatsapp-consent'
      elevation={1}
      sx={{ border: `1px solid ${colors.border.default}`, overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Typography variant='sectionTitle'>e-Surgery WhatsApp</Typography>
        {consent && canChangeWhatsAppConsent() && (
          <Button variant='ghost' onClick={() => setIsDialogOpen(true)}>
            {consent.optedIn ? 'Switch off' : 'Switch on'}
          </Button>
        )}
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>{renderContent()}</Box>

      {consent && (
        <ChangeWhatsAppConsentDialog
          open={isDialogOpen}
          customerHash={customerHash}
          consent={consent}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </Paper>
  )
}

interface ChangeWhatsAppConsentDialogProps {
  open: boolean
  customerHash: string
  consent: WhatsAppConsent
  onClose: () => void
}

function ChangeWhatsAppConsentDialog({ open, customerHash, consent, onClose }: ChangeWhatsAppConsentDialogProps) {
  const changeMutation = useChangeWhatsAppConsentMutation(customerHash)
  const isLoading = changeMutation.isPending
  const switchingOn = !consent.optedIn

  const handleClose = () => {
    if (isLoading) return
    onClose()
  }

  const handleConfirm = () => {
    if (!canChangeWhatsAppConsent()) return
    changeMutation.mutate(switchingOn, { onSuccess: onClose })
  }

  const description = switchingOn
    ? consent.source === 'whatsapp-reply'
      ? `The patient replied STOP to a WhatsApp on ${formatDateTime(consent.decidedAt)}. WhatsApp messages start again for this account, on ${consent.phone}, so only switch it on if they have asked for them again.`
      : consent.phone
        ? `WhatsApp messages start again for this account, on ${consent.phone}. Only switch it on if the patient asked.`
        : 'This account has no UK mobile stored, so no WhatsApp messages are sent until the patient checks out with one.'
    : consent.phone
      ? `WhatsApp messages stop for this account and ID reminders go by SMS. Every other e-Surgery account on ${consent.phone} is switched off too, as a STOP reply would.`
      : 'WhatsApp messages stop for this account and ID reminders go by SMS.'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='sectionTitle'>Switch WhatsApp {switchingOn ? 'on' : 'off'}?</Typography>
          <IconButton onClick={handleClose} size='small' disabled={isLoading}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: colors.text.primary, mb: 1.5 }}>{description}</Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.secondary }}>
          Your name is recorded with the change.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant='ghost' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Saving...' : `Switch ${switchingOn ? 'on' : 'off'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
